import { nodeConfig } from '@scripts/mixins';
import { getParam } from '@scripts/helpers/url-params';

const NAMESPACE = 'focus-on-load';
const DEFAULT_SELECTOR = '[data-js-focus-on-load]';

/**
 * @typedef {Object} FocusOnLoadSettings
 * @property {string} [focusTarget=''] - CSS selector for the element to focus.
 * @property {string} [urlParam=''] - URL search parameter name to check.
 */
const DEFAULT_SETTINGS = {
	focusTarget: '',
	urlParam: ''
};

/**
 * Focuses a target element on page load when a URL parameter is present.
 */
class FocusOnLoad {
	/**
	 * @param {HTMLElement} node
	 * @param {Object} config
	 */
	constructor(node, config) {
		this.elNode = node;
		this.config = config;
	}

	init() {
		if (this._shouldFocus()) {
			this._applyFocus();
		}
	}

	/** @returns {boolean} */
	_shouldFocus() {
		if (this.config.urlParam) {
			return getParam(this.config.urlParam) !== null;
		}

		return !!window.location.search;
	}

	/** @returns {HTMLElement|null} */
	_resolveTarget() {
		const selector = this.config.focusTarget;

		if (!selector) return null;

		return this.elNode.querySelector(selector) || document.querySelector(selector);
	}

	_applyFocus() {
		const target = this._resolveTarget();

		if (!target) return;

		if (!target.hasAttribute('tabindex')) {
			target.setAttribute('tabindex', '-1');
		}

		target.focus({
			preventScroll: true
		});

		requestAnimationFrame(() => {
			target.scrollIntoView({
				behavior: 'smooth',
			});
		})
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	const listNode = document.querySelectorAll(selector);

	if (!listNode.length) return;

	return Array.from(listNode).map((node) => {
		const config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);
		const module = new FocusOnLoad(node, config);

		module.init();

		return module;
	});
}
